import { Link, Outlet, createFileRoute } from '@tanstack/react-router'
import { opsApi } from '@/components/ops/api'
import { ErrorState, LoadingState } from '@/components/app/states'
import { cn } from '@/lib/utils'

const NAV = [
  { to: '/ops', label: 'Orders', exact: true },
  { to: '/ops/queue', label: 'Needs human' },
  { to: '/ops/summary', label: 'Summary' },
  { to: '/ops/library', label: 'Library' },
  { to: '/ops/spot-checks', label: 'Spot checks' },
  { to: '/ops/scratch', label: 'New site' },
  { to: '/ops/support', label: 'Support' },
  { to: '/ops/emails', label: 'Emails' },
  { to: '/ops/releases', label: 'Releases' },
  { to: '/ops/settings', label: 'Settings' },
] as const

export const Route = createFileRoute('/ops')({
  beforeLoad: async ({ location }) => {
    const me = await opsApi<{ email: string }>('/ops/me', undefined, location.href)
    return { opsEmail: me.email }
  },
  head: () => ({ meta: [{ title: 'Ops · Hedgehog' }, { name: 'robots', content: 'noindex' }] }),
  component: OpsLayout,
  pendingComponent: () => <LoadingState label="Checking access" />,
  errorComponent: ({ error }) => <ErrorState error={error} title="Could not open the ops console" />,
})

function OpsLayout() {
  const { opsEmail } = Route.useRouteContext()
  return (
    <div className="flex min-h-dvh flex-col bg-background text-sm text-foreground">
      <a
        href="#ops-main"
        className="sr-only focus:not-sr-only focus:fixed focus:top-2 focus:left-2 focus:z-50 focus:rounded-md focus:bg-background focus:px-3 focus:py-2"
      >
        Skip to content
      </a>
      <header className="sticky top-0 z-40 border-b border-border bg-background/95 backdrop-blur">
        <div className="flex h-11 items-center gap-4 px-4">
          <Link to="/ops" className="font-semibold">
            Hedgehog ops
          </Link>
          <nav aria-label="Ops" className="flex min-w-0 flex-1 gap-1 overflow-x-auto">
            {NAV.map((n) => (
              <Link
                key={n.to}
                to={n.to}
                activeOptions={{ exact: 'exact' in n && n.exact, includeSearch: false }}
                className={cn(
                  'rounded-md px-2 py-1 whitespace-nowrap text-muted-foreground outline-none hover:bg-muted hover:text-foreground focus-visible:ring-3 focus-visible:ring-ring/50',
                  'data-[status=active]:bg-muted data-[status=active]:font-medium data-[status=active]:text-foreground',
                )}
              >
                {n.label}
              </Link>
            ))}
          </nav>
          <span className="truncate text-muted-foreground" title="Signed in as">
            {opsEmail}
          </span>
        </div>
      </header>
      <main id="ops-main" className="flex-1 px-4 py-4">
        <Outlet />
      </main>
    </div>
  )
}
